import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Image as ImageIcon, Film, Loader2 } from "lucide-react";

/**
 * MediaUploader — CMS-facing widget with 3 modes:
 *
 *   mode="single-image"   → one image, shows current preview + Replace/Clear
 *   mode="single-video"   → one video, shows current preview + Replace/Clear
 *   mode="multi-image"    → thumbnail grid, upload many at once, per-item delete, alt-text edit
 *
 * All uploads go to POST /api/cms/upload-media which returns [{url, kind, size_kb, ...}].
 * Uploaded files are served from /api/cms/media/{filename} and adjust to any dimension
 * via CSS object-cover.
 */
export default function MediaUploader({ mode, value, onChange, accept, testIdPrefix = "media" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const multiple = mode === "multi-image";

  const acceptStr = accept || (
    mode === "single-video" ? "video/*"
      : mode === "multi-image" ? "image/*"
      : "image/*"
  );

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    try {
      const r = await api.post("/cms/upload-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const items = r.data.items || [];
      if (mode === "single-image" || mode === "single-video") {
        onChange(items[0]?.url || "");
        toast.success(`Uploaded ${items[0]?.filename || "file"}`);
      } else if (mode === "multi-image") {
        const existing = Array.isArray(value) ? value : [];
        const additions = items.map((it) => ({ url: it.url, alt: it.filename?.replace(/\.[^.]+$/, "") || "" }));
        onChange([...existing, ...additions]);
        toast.success(`${items.length} image${items.length > 1 ? "s" : ""} added`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  /* ── SINGLE IMAGE / SINGLE VIDEO ── */
  if (mode === "single-image" || mode === "single-video") {
    const isVideo = mode === "single-video";
    const url = value || "";
    return (
      <div className="space-y-2">
        <input
          ref={inputRef}
          type="file"
          accept={acceptStr}
          onChange={(e) => doUpload(e.target.files)}
          className="hidden"
          data-testid={`${testIdPrefix}-file-input`}
        />
        {url ? (
          <div className="relative overflow-hidden rounded-lg border border-border bg-black/5" style={{ aspectRatio: "16/9" }}>
            {isVideo ? (
              <video src={url} className="h-full w-full object-cover" muted autoPlay loop playsInline />
            ) : (
              <img src={url} alt="Preview" className="h-full w-full object-cover" />
            )}
            <div className="absolute right-2 top-2 flex gap-1">
              <Button size="sm" variant="secondary" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testIdPrefix}-replace`}>
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Upload className="mr-1 h-3 w-3" />}
                Replace
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onChange("")} data-testid={`${testIdPrefix}-clear`}>
                <X size={12} />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="h-24 w-full border-dashed"
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : isVideo ? <Film className="mr-2 h-4 w-4" /> : <ImageIcon className="mr-2 h-4 w-4" />}
            {busy ? "Uploading…" : isVideo ? "Upload video (mp4 / webm / mov, max 60 MB)" : "Upload image (jpg / png / webp, max 10 MB)"}
          </Button>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="whitespace-nowrap">or paste URL:</span>
          <Input
            value={url}
            onChange={(e) => onChange(e.target.value)}
            placeholder={isVideo ? "https://…/hero.mp4" : "https://…/hero.jpg"}
            className="h-8 text-xs"
            data-testid={`${testIdPrefix}-url-input`}
          />
        </div>
      </div>
    );
  }

  /* ── MULTI IMAGE (slideshow) ── */
  const items = Array.isArray(value) ? value : [];
  const setAlt = (i, alt) => onChange(items.map((it, idx) => idx === i ? { ...it, alt } : it));
  const removeAt = async (i) => {
    const it = items[i];
    // Best-effort backend cleanup for uploads (URLs starting with /api/cms/media)
    if (it?.url?.includes("/api/cms/media/")) {
      try { await api.post("/cms/media/delete", { url: it.url }); } catch { /* ignore */ }
    }
    onChange(items.filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept={acceptStr}
        multiple
        onChange={(e) => doUpload(e.target.files)}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
          {busy ? "Uploading…" : "Upload multiple images"}
        </Button>
        <span className="text-xs text-muted-foreground">
          Selects one or many at once · Auto-creates slideshow · jpg / png / webp / gif · max 10 MB each
        </span>
      </div>

      {items.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {items.map((it, i) => (
            <div
              key={`${it.url}-${i}`}
              className="group relative overflow-hidden rounded-lg border border-border bg-muted/30"
              data-testid={`${testIdPrefix}-item-${i}`}
            >
              <div className="relative" style={{ aspectRatio: "16/9" }}>
                {it.url ? (
                  <img src={it.url} alt={it.alt || `Slide ${i + 1}`} className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full w-full place-items-center text-muted-foreground">
                    <ImageIcon size={24} />
                  </div>
                )}
                <div className="absolute inset-0 flex items-start justify-end p-2 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    className="rounded-full bg-red-500 p-1 text-white shadow-lg hover:bg-red-600"
                    aria-label="Remove slide"
                    data-testid={`${testIdPrefix}-remove-${i}`}
                  >
                    <X size={12} />
                  </button>
                </div>
                <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white">
                  #{i + 1}
                </div>
              </div>
              <div className="space-y-1 p-2">
                <Input
                  value={it.alt || ""}
                  onChange={(e) => setAlt(i, e.target.value)}
                  placeholder="Alt text (SEO / a11y)"
                  className="h-7 text-xs"
                  data-testid={`${testIdPrefix}-alt-${i}`}
                />
                <Input
                  value={it.url || ""}
                  onChange={(e) => onChange(items.map((row, idx) => idx === i ? { ...row, url: e.target.value } : row))}
                  placeholder="URL"
                  className="h-7 font-mono text-[10px]"
                />
              </div>
            </div>
          ))}
          {/* Add-more tile */}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="grid place-items-center rounded-lg border-2 border-dashed border-border/60 bg-muted/20 text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            style={{ aspectRatio: "16/9" }}
            data-testid={`${testIdPrefix}-add-more`}
          >
            <div className="text-center text-xs">
              <Upload className="mx-auto mb-1 h-5 w-5" />
              Add more
            </div>
          </button>
        </div>
      ) : (
        <div
          className="grid place-items-center rounded-lg border-2 border-dashed border-border/60 bg-muted/20 py-10 text-center text-xs text-muted-foreground"
          style={{ minHeight: 140 }}
        >
          <div>
            <ImageIcon className="mx-auto mb-2 h-8 w-8" />
            No images yet — click <b>Upload multiple images</b> above.<br />
            <span className="text-[10px]">Photos auto-resize to fit the hero card.</span>
          </div>
        </div>
      )}
    </div>
  );
}
